/**
 * Single sign-on — the one connection to the organization's identity
 * provider, OIDC or SAML, and the two switches beside it: on, and required.
 *
 * The page starts with what the identity provider needs from us (the redirect
 * URI, or the entity ID and the ACS URL, and the SP metadata URL), because
 * that is the first thing an administrator configuring Okta or Entra ID is
 * asked for. Then what we need from it. The client secret is a field that is
 * written and never read back: the page only knows whether one is set.
 *
 * *Required* needs a verified domain — single sign-on signs in only addresses
 * at those — and the page says that the owner can still sign in with a
 * password or GitHub, followed by a second factor, so an identity provider
 * that is down never locks out the person who can fix it.
 */
import { useState } from 'react';
import type React from 'react';
import { color, font } from '../../theme/tokens';
import { Callout, Field, GhostButton, Input, PrimaryButton } from '../../components/primitives';
import { SettingToggle } from '../../components/settings-row';
import { useConfirm } from '../../components/Modal';
import { api, apiPath, prefix } from '../../lib/api';
import type { OrgIdentity } from '../../lib/types';
import type { SectionProps } from './Security';

function Copyable({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '190px minmax(0,1fr)', gap: 12, alignItems: 'baseline' }}>
      <span style={{ fontSize: 13, color: color.inkSubtle }}>{label}</span>
      <span style={{ fontFamily: font.mono, fontSize: 12, userSelect: 'all', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

const area: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  minHeight: 110,
  fontFamily: font.mono,
  fontSize: 12,
  padding: 10,
  border: `1px solid ${color.lineInput}`,
  borderRadius: 8,
  background: color.surface,
  color: color.ink
};

export function SsoSection({ organization, identity, apply }: SectionProps): React.JSX.Element {
  const sso = identity.sso;
  const [protocol, setProtocol] = useState<'oidc' | 'saml'>(sso.protocol ?? 'oidc');
  const [issuer, setIssuer] = useState(sso.oidc?.issuer ?? '');
  const [clientId, setClientId] = useState(sso.oidc?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [metadataXml, setMetadataXml] = useState('');
  const [metadataUrl, setMetadataUrl] = useState(sso.saml?.metadataUrl ?? '');
  const [dialog, ask] = useConfirm();
  const base = apiPath`/orgs/${organization.id}/identity`;
  const verified = identity.domains.some((domain) => domain.verifiedAt);

  const save = (switches: { enabled: boolean; required: boolean }): void => {
    void apply(async () => {
      const answer = await api.send<OrgIdentity>('PUT', apiPath`${prefix(base)}/sso`, {
        protocol,
        ...switches,
        oidc: protocol === 'oidc' ? { issuer, clientId, clientSecret: clientSecret || undefined } : undefined,
        saml: protocol === 'saml' ? { metadataXml: metadataXml || undefined, metadataUrl: metadataXml ? undefined : metadataUrl || undefined } : undefined
      }, {});
      setClientSecret('');
      setMetadataXml('');
      return answer;
    });
  };

  const remove = async (): Promise<void> => {
    const agreed = await ask({
      title: 'Remove single sign-on?',
      lede: `Members of ${organization.name} sign in with a password or GitHub again. Nobody is signed out.`,
      confirm: 'Remove it',
      danger: true
    });
    if (agreed) void apply(() => api.del<OrgIdentity>(apiPath`${prefix(base)}/sso`));
  };

  // The connection is the owner's to set (the Worker answers an administrator
  // 403): whoever configures the identity provider can sign in as anybody at
  // the organization's domains, the owner included. Everybody else reads it.
  if (organization.role !== 'owner') {
    return (
      <>
        <Callout title="Only the owner changes single sign-on">
          Whoever sets the identity provider decides who it signs in as, so the owner of {organization.name} is the one
          person who can change or remove it. It is {sso.configured ? (sso.enabled ? 'on' : 'set up and off') : 'not set up'}
          {sso.required ? ', and required for addresses at the verified domains' : ''}.
        </Callout>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>What the identity provider is given</div>
          <Copyable label="Redirect URI (OpenID Connect)" value={sso.sp.redirectUri} />
          <Copyable label="Entity ID (SAML)" value={sso.sp.entityId} />
          <Copyable label="ACS URL (SAML)" value={sso.sp.acsUrl} />
        </div>
      </>
    );
  }

  return (
    <>
      {dialog}
      {!verified && (
        <Callout tone="amber" title="Prove a domain first">
          Single sign-on signs in only addresses at this organization’s verified domains. Add one on Domains.
        </Callout>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        {(['oidc', 'saml'] as const).map((choice) => (
          <GhostButton key={choice} size="sm" onClick={() => setProtocol(choice)} disabled={protocol === choice}>
            {choice === 'oidc' ? 'OpenID Connect' : 'SAML 2.0'}
          </GhostButton>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Give your identity provider</div>
        {protocol === 'oidc' ? (
          <Copyable label="Redirect URI" value={sso.sp.redirectUri} />
        ) : (
          <>
            <Copyable label="Entity ID (audience)" value={sso.sp.entityId} />
            <Copyable label="ACS URL (HTTP-POST)" value={sso.sp.acsUrl} />
            <Copyable label="SP metadata" value={sso.sp.metadataUrl} />
            <Copyable label="Name ID" value="the person’s email address" />
          </>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 640 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>What it gives you</div>
        {protocol === 'oidc' ? (
          <>
            <Field label="Issuer URL" htmlFor="sso-issuer">
              <Input id="sso-issuer" value={issuer} onChange={(event) => setIssuer(event.target.value)} placeholder="https://acme.okta.com" />
            </Field>
            <Field label="Client ID" htmlFor="sso-client">
              <Input id="sso-client" value={clientId} onChange={(event) => setClientId(event.target.value)} />
            </Field>
            <Field label={sso.oidc?.hasSecret ? 'Client secret (set — type to replace)' : 'Client secret'} htmlFor="sso-secret">
              <Input id="sso-secret" type="password" autoComplete="off" value={clientSecret} onChange={(event) => setClientSecret(event.target.value)} />
            </Field>
            {!sso.secretsConfigured && (
              <p style={{ margin: 0, fontSize: 13, color: color.badText }}>This deployment cannot keep a client secret yet (SSO_SECRETS_KEY is not set).</p>
            )}
          </>
        ) : (
          <>
            {sso.saml && (
              <p style={{ margin: 0, fontSize: 13, color: color.inkSubtle }}>
                Connected to {sso.saml.entityId}, {sso.saml.certificates} signing certificate{sso.saml.certificates === 1 ? '' : 's'}. Paste new
                metadata to replace it.
              </p>
            )}
            <Field label="Metadata URL" htmlFor="sso-md-url">
              <Input id="sso-md-url" value={metadataUrl} onChange={(event) => setMetadataUrl(event.target.value)} placeholder="https://…/metadata" />
            </Field>
            <Field label="Or paste the metadata XML" htmlFor="sso-md">
              <textarea id="sso-md" style={area} value={metadataXml} onChange={(event) => setMetadataXml(event.target.value)} />
            </Field>
          </>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 640 }}>
        <label style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 14 }}>
          <SettingToggle on={sso.enabled} onToggle={() => save({ enabled: !sso.enabled, required: sso.enabled ? false : sso.required })} />
          Members can sign in with single sign-on
        </label>
        <label style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 14 }}>
          <SettingToggle on={sso.required} onToggle={() => save({ enabled: sso.enabled || !sso.required, required: !sso.required })} />
          Require it for addresses at our verified domains
        </label>
        <p style={{ margin: 0, fontSize: 13, color: color.inkSubtle, lineHeight: 1.6 }}>
          When it is required, those addresses cannot use a password or GitHub. The owner still can, followed by a two-factor
          code, so a broken identity provider never locks you out.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <PrimaryButton tone="ink" onClick={() => save({ enabled: sso.enabled, required: sso.required })}>
          Save
        </PrimaryButton>
        {sso.configured && <GhostButton onClick={() => void remove()}>Remove</GhostButton>}
      </div>
    </>
  );
}
